import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, Circle, Clock, Users, Undo2, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/shop/payroll`;
const LANE_TONE = {
  shop: 'border-orange-300 bg-orange-50',
  crib: 'border-sky-300 bg-sky-50',
  millwright: 'border-emerald-300 bg-emerald-50',
};

// Two-tier payroll sign-off: each manager ticks their own people; the week is Approved for Payroll once everyone is ticked.
export function PayrollLanes({ dateFrom, dateTo, onWeekChange }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/lanes`, { params: { date_from: dateFrom, date_to: dateTo } });
      setState(r.data);
      onWeekChange?.(r.data.week_approval);
    } catch { /* report not loaded yet */ }
  }, [dateFrom, dateTo, onWeekChange]);

  useEffect(() => { load(); }, [load]);

  const act = async (path, body, okMsg) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/${path}`, { date_from: dateFrom, date_to: dateTo, ...body });
      setState(r.data);
      onWeekChange?.(r.data.week_approval);
      if (r.data.complete) toast.success('Everyone is signed off — week Approved for Payroll, Chris & Linda notified');
      else if (okMsg) toast.success(okMsg);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update'); }
    setBusy(false);
  };

  if (!state) return null;
  const { lanes, people, complete, week_approval: week, hours_changed: changed } = state;
  const signed = people.filter(p => p.approved).length;

  return (
    <div className="bg-white rounded-xl border border-slate-300 p-4 space-y-3" data-testid="payroll-lanes">
      <div className="flex flex-wrap items-center gap-3">
        <Users className="w-5 h-5 text-slate-700" />
        <div className="flex-1 min-w-[200px]">
          <p className="text-sm font-bold text-slate-900">Payroll sign-off — {signed} of {people.length} people ticked</p>
          <p className="text-[11px] text-slate-600">Each manager signs off their own people. The week is Approved for Payroll once everyone is ticked.</p>
        </div>
        {complete || week ? (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-100 border border-emerald-300 text-emerald-800 text-xs font-bold" data-testid="lanes-week-complete">
            <CheckCircle className="w-4 h-4" /> Approved for Payroll{week?.approved_by ? ` · ${week.approved_by}` : ''}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-100 border border-amber-300 text-amber-800 text-xs font-bold" data-testid="lanes-week-pending">
            <Clock className="w-4 h-4" /> {people.length - signed} still to sign off
          </span>
        )}
      </div>
      {changed.length > 0 && (
        <div className="flex items-center gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2" data-testid="lanes-hours-changed">
          <AlertTriangle className="w-4 h-4 text-amber-600" /> Hours changed after sign-off for {changed.join(', ')} — un-tick and re-tick them.
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {lanes.map(lane => {
          const members = people.filter(p => p.lane === lane.key);
          return (
            <div key={lane.key} className={`rounded-lg border p-3 ${LANE_TONE[lane.key]}`} data-testid={`lane-${lane.key}`}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <div>
                  <p className="text-xs font-bold text-slate-900 uppercase tracking-wide">{lane.label}</p>
                  <p className="text-[10px] text-slate-600">{lane.approver_label} signs off · {lane.approved}/{lane.people} done</p>
                </div>
                {lane.can_approve && lane.pending > 0 && (
                  <button onClick={() => act('approve', { lane: lane.key }, `${lane.label}: ${lane.pending} ticked`)} disabled={busy}
                    className="px-2.5 py-1.5 rounded-md bg-slate-900 text-white text-[11px] font-semibold hover:bg-slate-700 disabled:opacity-50"
                    data-testid={`lane-approve-all-${lane.key}`}>
                    Approve my people ({lane.pending})
                  </button>
                )}
              </div>
              {members.length === 0 ? (
                <p className="text-[11px] text-slate-500 italic">Nobody with hours this week</p>
              ) : (
                <ul className="space-y-1">
                  {members.map(p => (
                    <li key={p.employee_name} className="flex items-center gap-2 text-xs bg-white/70 rounded-md px-2 py-1" data-testid={`lane-person-${p.employee_name.replace(/\s+/g, '-')}`}>
                      <button
                        disabled={!p.can_approve || busy}
                        title={p.can_approve ? (p.approved ? 'Un-tick' : 'Tick as approved') : `${lane.approver_label} signs this one off`}
                        onClick={() => p.approved
                          ? act('rescind', { employee_names: [p.employee_name] }, `${p.employee_name} un-ticked`)
                          : act('approve', { employee_names: [p.employee_name] }, `${p.employee_name} ticked`)}
                        className={`flex-shrink-0 ${p.can_approve ? 'cursor-pointer' : 'cursor-not-allowed opacity-70'}`}
                        data-testid={`lane-tick-${p.employee_name.replace(/\s+/g, '-')}`}>
                        {p.approved ? <CheckCircle className="w-4 h-4 text-emerald-600" /> : <Circle className="w-4 h-4 text-slate-400" />}
                      </button>
                      <span className={`flex-1 font-semibold ${p.approved ? 'text-slate-800' : 'text-slate-900'}`}>{p.employee_name}</span>
                      <span className="text-slate-600 tabular-nums">{p.hours}h</span>
                      {p.approved && <span className="text-[9px] text-emerald-700 truncate max-w-[90px]" title={`${p.approved.by} · ${new Date(p.approved.at).toLocaleString()}`}>{p.approved.by.split(' ')[0]}</span>}
                      {p.hours_changed && <Undo2 className="w-3 h-3 text-amber-600" title="hours changed since sign-off" />}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default PayrollLanes;
